function sumChildrenAmounts(node) {
  let monthTotals = {};
  let yearTotals = {};

  if (node.children && node.children.length > 0) {
    node.children.forEach(child => {
      const { monthTotals: childMonthTotals, yearTotals: childYearTotals } = sumChildrenAmounts(child);
      for (const locationId in childMonthTotals) {
        if (!monthTotals[locationId]) monthTotals[locationId] = 0;
        monthTotals[locationId] += childMonthTotals[locationId];
      }
      for (const locationId in childYearTotals) {
        if (!yearTotals[locationId]) yearTotals[locationId] = 0;
        yearTotals[locationId] += childYearTotals[locationId];
      }
    });
  } else {
    monthTotals = node.monthTotals || {};
    yearTotals = node.yearTotals || {};
  }

  node.monthTotals = monthTotals;
  node.yearTotals = yearTotals;

  return { monthTotals, yearTotals };
}

function flattenTree(data, level = 0, result = [], parentNature = 1) {
  data.forEach(node => {
    const nodeNature = node.nature || parentNature;
    const alwaysInclude = [4, 5, 6, 7, 8, 449, 451].includes(node.id);
    const hasNonZeroTotals = Object.values(node.monthTotals).some(total => total !== 0) ||
                             Object.values(node.yearTotals).some(total => total !== 0);

    if (alwaysInclude || hasNonZeroTotals) {
      const filteredChildren = node.children ? flattenTree(node.children, level + 1, [], nodeNature).filter(child => {
        return Object.values(child.monthTotals).some(total => total !== 0) || Object.values(child.yearTotals).some(total => total !== 0);
      }) : [];

      result.push({
        id: node.id,
        name: node.name,
        parent: node.parent,
        nature: nodeNature,
        code: node.code,
        note: node.note,
        account_type_id: node.account_type_id,
        dgii_category_id: node.dgii_category_id,
        status: node.status,
        level: level,
        monthTotals: node.monthTotals,
        yearTotals: node.yearTotals,
        isParent: alwaysInclude,
        isTotal: node.isTotal || false,
        children: filteredChildren
      });
    }
  });
  return result;
}

export function generateEstadoResultadoView(all_accounts, processed_accounts) {
  const addAmountsToAccounts = (accounts, processed) => {
    accounts.forEach(account => {
      let monthTotals = {};
      let yearTotals = {};

      processed.forEach(procAccount => {
        if (procAccount.chart_of_account_id === account.id) {
          const locationId = procAccount.location_id;
          monthTotals[locationId] = (monthTotals[locationId] || 0) + parseFloat(procAccount.month_total || 0);
          yearTotals[locationId] = (yearTotals[locationId] || 0) + parseFloat(procAccount.year_total || 0);
        }
      });

      account.monthTotals = monthTotals;
      account.yearTotals = yearTotals;

      if (account.children && account.children.length > 0) {
        addAmountsToAccounts(account.children, processed);
      }
    });
  };

  addAmountsToAccounts(all_accounts, processed_accounts);
  all_accounts.forEach(node => sumChildrenAmounts(node));

  let ingresos = all_accounts.find(acc => acc.id === 5);
  let costos = all_accounts.find(acc => acc.id === 6);
  let gastos = all_accounts.find(acc => acc.id === 7);
  let otrosIngresos = all_accounts.find(acc => acc.id === 449);
  let otrosEgresos = all_accounts.find(acc => acc.id === 451);

  if (!ingresos || !costos || !gastos || !otrosIngresos || !otrosEgresos) {
    console.error("Ingresos, Costos, Gastos, Otros Ingresos, or Otros Egresos account not found.");
    return [];
  }

  ingresos.nature = -1;
  costos.nature = 1;
  gastos.nature = 1;
  otrosIngresos.nature = -1;
  otrosEgresos.nature = 1;

  let contribucionMarginal = {
    name: "Contribución Marginal",
    monthTotals: {},
    yearTotals: {},
    level: costos.level + 1,
    isParent: false,
    nature: 1,
    isTotal: true,
    children: []
  };

  let utilidadOperaciones = {
    name: "Utilidad de Operaciones",
    monthTotals: {},
    yearTotals: {},
    level: gastos.level + 1,
    isParent: false,
    nature: 1,
    isTotal: true,
    children: []
  };

  let resultadoNeto = {
    name: "Resultado Neto",
    monthTotals: {},
    yearTotals: {},
    level: utilidadOperaciones.level + 1,
    isParent: false,
    nature: 1,
    isTotal: true,
    children: []
  };

  const locationIds = new Set([
    ...Object.keys(ingresos.monthTotals),
    ...Object.keys(costos.monthTotals),
    ...Object.keys(gastos.monthTotals),
    ...Object.keys(otrosIngresos.monthTotals),
    ...Object.keys(otrosEgresos.monthTotals)
  ]);

  locationIds.forEach(locationId => {
    const ingresosMonthTotal = ingresos.monthTotals[locationId] || 0;
    const costosMonthTotal = costos.monthTotals[locationId] || 0;
    const gastosMonthTotal = gastos.monthTotals[locationId] || 0;
    const otrosIngresosMonthTotal = otrosIngresos.monthTotals[locationId] || 0;
    const otrosEgresosMonthTotal = otrosEgresos.monthTotals[locationId] || 0;

    const ingresosYearTotal = ingresos.yearTotals[locationId] || 0;
    const costosYearTotal = costos.yearTotals[locationId] || 0;
    const gastosYearTotal = gastos.yearTotals[locationId] || 0;
    const otrosIngresosYearTotal = otrosIngresos.yearTotals[locationId] || 0;
    const otrosEgresosYearTotal = otrosEgresos.yearTotals[locationId] || 0;

    contribucionMarginal.monthTotals[locationId] = -ingresosMonthTotal - costosMonthTotal;
    contribucionMarginal.yearTotals[locationId] = -ingresosYearTotal - costosYearTotal;

    utilidadOperaciones.monthTotals[locationId] = contribucionMarginal.monthTotals[locationId] - gastosMonthTotal;
    utilidadOperaciones.yearTotals[locationId] = contribucionMarginal.yearTotals[locationId] - gastosYearTotal;

    resultadoNeto.monthTotals[locationId] = utilidadOperaciones.monthTotals[locationId] - otrosIngresosMonthTotal - otrosEgresosMonthTotal;
    resultadoNeto.yearTotals[locationId] = utilidadOperaciones.yearTotals[locationId] - otrosIngresosYearTotal - otrosEgresosYearTotal;
  });

  const costosIndex = all_accounts.indexOf(costos);
  const gastosIndex = all_accounts.indexOf(gastos);

  if (costosIndex !== -1) {
    all_accounts.splice(costosIndex + 1, 0, contribucionMarginal);
  }

  if (gastosIndex !== -1) {
    all_accounts.splice(gastosIndex + 2, 0, utilidadOperaciones);
  }

  // Insertar una fila en blanco
  const blankRow = {
    name: "",
    monthTotals: {},
    yearTotals: {},
    level: 0,
    isParent: false,
    children: []
  };

  // Insertar la fila en blanco y el resultado neto al final
  all_accounts.push(blankRow);
  all_accounts.push(resultadoNeto);

  const allFlattenedAccounts = flattenTree(all_accounts);
  const rootAccounts = allFlattenedAccounts.filter(account => account.level === 0);
 
  return rootAccounts;
}


